import React, { useContext, useEffect, useState } from 'react';
import AnnonceContext from 'src/contexts/AnnonceContext';
import { getStockages } from 'src/service/stockageApi';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { InboxStackIcon } from '@heroicons/react/24/outline';

// Palette de couleurs pour les points de stockage (10 couleurs)
const STOCKAGE_COLORS = [
  '#1b9faa', // Bleu-vert (couleur principale)
  '#ff6b6b', // Rouge
  '#4ecdc4', // Turquoise
  '#45b7d1', // Bleu clair
  '#96ceb4', // Vert menthe
  '#feca57', // Jaune
  '#ff9ff3', // Rose
  '#54a0ff', // Bleu
  '#5f27cd', // Violet
  '#00d2d3', // Cyan
];

// Récupérer les points de stockage depuis la variable d'environnement
const getStockagePoints = () => {
  try {
    return JSON.parse(process.env.REACT_APP_STOCKAGE_POINTS || '[]');
  } catch (error) {
    console.error('Erreur lors du parsing des points de stockage:', error);
    return [];
  }
};

// Fonction pour obtenir la couleur d'un point de stockage
const getStockageColor = (pointIndex) => {
  return STOCKAGE_COLORS[pointIndex % STOCKAGE_COLORS.length];
};

export default function BoxSelector() {
  const { formAnnonce, setFormAnnonce, currentCategory } = useContext(AnnonceContext);
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedStockagePoint, setSelectedStockagePoint] = useState(null);
  const selectedBoxId = formAnnonce.stockage ? parseInt(formAnnonce.stockage.split('/').pop()) : null;
  const selectedBox = options.find(box => box.id === selectedBoxId);
  
  // Clés pour le localStorage
  const STORAGE_KEYS = {
    LAST_STOCKAGE_POINT: 'lastStockagePoint',
    LAST_BOX_ID: 'lastBoxId',
    LAST_CATEGORY: 'lastCategory'
  };
  
  const stockagePoints = getStockagePoints();
  
  // Fonction pour extraire le point de stockage du nom de la boîte
  const extractStockagePoint = (boxName) => {
    for (const point of stockagePoints) {
      if (boxName.includes(` - ${point}`)) {
        return point;
      }
    }
    return null;
  };

  // Fonctions pour gérer le localStorage
  const saveToLocalStorage = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde dans localStorage:', error);
    }
  };

  const getFromLocalStorage = (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Erreur lors de la récupération depuis localStorage:', error);
      return defaultValue;
    }
  };

  const saveUserChoices = (stockagePoint, boxId, category) => {
    saveToLocalStorage(STORAGE_KEYS.LAST_STOCKAGE_POINT, stockagePoint);
    saveToLocalStorage(STORAGE_KEYS.LAST_BOX_ID, boxId);
    saveToLocalStorage(STORAGE_KEYS.LAST_CATEGORY, category);
  };
  
  useEffect(() => {
    getStockages("?motherBox=1").then((res) => {
      setOptions(res);
      
      // Charger les choix précédents de l'utilisateur
      const lastCategory = getFromLocalStorage(STORAGE_KEYS.LAST_CATEGORY);
      const lastBoxId = getFromLocalStorage(STORAGE_KEYS.LAST_BOX_ID);
      
      // Pas de sélection automatique de boîte, seulement le filtrage par point de stockage
    });
  }, [currentCategory, selectedStockagePoint, formAnnonce, setFormAnnonce]);

  // useEffect pour charger le point de stockage depuis localStorage et l'appliquer
  useEffect(() => {
    console.log('useEffect localStorage - currentCategory:', currentCategory?.name);
    console.log('useEffect localStorage - stockagePoints:', stockagePoints);
    
    if (currentCategory) {
      const lastCategory = getFromLocalStorage(STORAGE_KEYS.LAST_CATEGORY);
      const lastStockagePoint = getFromLocalStorage(STORAGE_KEYS.LAST_STOCKAGE_POINT);
      
      console.log('useEffect localStorage - lastCategory:', lastCategory);
      console.log('useEffect localStorage - lastStockagePoint:', lastStockagePoint);
      
      // Appliquer le point de stockage précédent même si la catégorie change
      if (lastStockagePoint && stockagePoints.includes(lastStockagePoint)) {
        console.log('Application du point de stockage depuis localStorage:', lastStockagePoint);
        setSelectedStockagePoint(lastStockagePoint);
      } else {
        console.log('Point de stockage invalide ou non trouvé dans stockagePoints');
      }
      
      // Log pour information
      if (lastCategory && currentCategory.name === lastCategory) {
        console.log('Catégories correspondent');
      } else {
        console.log('Catégories différentes - lastCategory:', lastCategory, 'currentCategory:', currentCategory.name);
      }
    } else {
      console.log('Pas de currentCategory');
    }
  }, [currentCategory, stockagePoints]);

  // useEffect pour recalculer le filtrage quand les critères changent
  useEffect(() => {
    console.log('useEffect filtrage - selectedStockagePoint:', selectedStockagePoint);
    console.log('useEffect filtrage - options count:', options.length);
    
    let filtered = options;
    let debugPrefix = '';
    
    // Filtrage logique selon la catégorie
    if (currentCategory && currentCategory.name) {
      const prefix = currentCategory.name.substring(0, 3).toUpperCase();
      debugPrefix = prefix;
      const matching = options.filter(box => box.boxName.toUpperCase().startsWith(prefix));
      matching.sort((a, b) => (a.annonces?.length || 0) - (b.annonces?.length || 0));
      const nonMatching = options.filter(box => !box.boxName.toUpperCase().startsWith(prefix));
      filtered = [...matching, ...nonMatching];
      console.log('Après filtrage catégorie:', filtered.length, 'boîtes');
    }
    
    // Filtre par point de stockage
    if (selectedStockagePoint) {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(box => {
        const boxStockagePoint = extractStockagePoint(box.boxName);
        return boxStockagePoint === selectedStockagePoint;
      });
      console.log('Après filtrage point de stockage:', filtered.length, 'boîtes (était', beforeFilter, ')');
    }
    
    // Filtre par autocomplétion
    if (search) {
      filtered = filtered.filter(box => box.boxName.toLowerCase().includes(search.toLowerCase()));
    }
    
    setFilteredOptions(filtered);
    console.log('Filtrage final:', filtered.length, 'boîtes');
  }, [options, currentCategory, selectedStockagePoint, search]);
 
  return (
    <div>
      {/* Affichage de la boîte sélectionnée ou bouton de sélection */}
      {selectedBox ? (
        <div
          className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
          onClick={() => setOpen(true)}
        >
          {(() => {
            const boxStockagePoint = extractStockagePoint(selectedBox.boxName);
            const stockagePointIndex = stockagePoints.indexOf(boxStockagePoint);
            const boxColor = stockagePointIndex >= 0 ? getStockageColor(stockagePointIndex) : '#1b9faa';
            
            return (
              <>
                <InboxStackIcon 
                  className="w-8 h-8" 
                  style={{ color: boxColor }}
                />
                <div className="flex flex-col flex-1">
                  <span className="font-semibold text-gray-800">
                    {boxStockagePoint ? selectedBox.boxName.replace(` - ${boxStockagePoint}`, '') : selectedBox.boxName}
                  </span>
                  {boxStockagePoint && (
                    <span 
                      className="text-xs font-medium px-2 py-1 rounded-full text-white mt-1 w-fit"
                      style={{ backgroundColor: boxColor }}
                    >
                      {boxStockagePoint}
                    </span>
                  )}
                </div>
                <span 
                  className="text-xs text-white rounded-full px-2 py-1"
                  style={{ backgroundColor: boxColor }}
                >
                  {selectedBox.annonces ? selectedBox.annonces.length : 0} objets
                </span>
              </>
            );
          })()}
        </div>
      ) : (
        <button
          type="button"
          className="px-4 py-2 bg-[#1b9faa] text-white rounded-md hover:bg-[#15808a] transition-colors"
          onClick={() => setOpen(true)}
        >
          Sélectionner une boîte
        </button>
      )}
      {/* Boîte de dialogue de sélection */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Choisir une boîte</DialogTitle>
        <DialogContent>
          {/* Debug : préfixe utilisé */}
          {/* <div className="mb-2 text-xs text-gray-400">Préfixe utilisé : {debugPrefix}</div> */}
          
          {/* Boutons de filtrage par point de stockage */}
          {stockagePoints.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Points de stockage</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => {
                    setSelectedStockagePoint(null);
                    saveUserChoices(null, selectedBoxId, currentCategory?.name);
                  }}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    selectedStockagePoint === null
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Tous
                </button>
                {stockagePoints.map((point, index) => (
                  <button
                    key={point}
                    onClick={() => {
                      setSelectedStockagePoint(point);
                      saveUserChoices(point, selectedBoxId, currentCategory?.name);
                    }}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      selectedStockagePoint === point
                        ? 'text-white shadow-lg'
                        : 'text-white hover:opacity-80'
                    }`}
                    style={{
                      backgroundColor: selectedStockagePoint === point 
                        ? getStockageColor(index) 
                        : getStockageColor(index) + '80'
                    }}
                  >
                    {point}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <input
            type="text"
            placeholder="Rechercher une boîte..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b9faa]"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {filteredOptions.map((box) => {
              // Déterminer la couleur en fonction du point de stockage
              const boxStockagePoint = extractStockagePoint(box.boxName);
              const stockagePointIndex = stockagePoints.indexOf(boxStockagePoint);
              const boxColor = stockagePointIndex >= 0 ? getStockageColor(stockagePointIndex) : '#1b9faa';
              
              return (
                <div
                  key={box.id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-100 transition-all ${selectedBoxId === box.id ? 'ring-2 ring-[#1b9faa]' : ''}`}
                  onClick={() => {
                    setFormAnnonce({ ...formAnnonce, stockage: `api/stockages/${box.id}` });
                    saveUserChoices(selectedStockagePoint, box.id, currentCategory?.name);
                    setOpen(false);
                  }}
                >
                  <InboxStackIcon 
                    className="w-8 h-8" 
                    style={{ color: boxColor }}
                  />
                  <div className="flex flex-col flex-1">
                    <span className="font-semibold text-gray-800">
                      {boxStockagePoint ? box.boxName.replace(` - ${boxStockagePoint}`, '') : box.boxName}
                    </span>
                    {boxStockagePoint && (
                      <span 
                        className="text-xs font-medium px-2 py-1 rounded-full text-white mt-1 w-fit"
                        style={{ backgroundColor: boxColor }}
                      >
                        {boxStockagePoint}
                      </span>
                    )}
                  </div>
                  <span 
                    className="text-xs text-white rounded-full px-2 py-1"
                    style={{ backgroundColor: boxColor }}
                  >
                    {box.annonces ? box.annonces.length : 0} objets
                  </span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
